import styles from "./styles/CreationFromLinkBox.module.css";
import {
    Button,
    Form,
    Input,
    Modal,
    Tabs,
    Upload,
    message,
    Typography,
    Switch,
    Space,
    Grid,
} from "antd";
import React, {
    forwardRef,
    useEffect,
    useMemo,
    useState,
    useImperativeHandle,
} from "react";
import {
    ArrowRightOutlined,
    UploadOutlined,
    CloudUploadOutlined,
    DeleteOutlined,
    FilePdfOutlined,
    FileWordOutlined,
    FileExcelOutlined,
    FileTextOutlined,
    FileMarkdownOutlined,
    FileImageOutlined,
    FileZipOutlined,
    FileOutlined,
} from "@ant-design/icons";
import {
    useAnalyseDataSource,
    useTrendIdeaGeneration,
    useNewPresetGeneration,
} from "../_actions/bot";
import log from "cslog";
import { useCreateItem } from "../_actions/mutations/items";
import CalendarCard from "./SocialMediaCards";
import IdeaCanvasModal from "./IdeaCanvasModal";
import { toLower } from "lodash";
import { useUploadFile } from "../_actions/files";
import ReactMarkdown from "react-markdown";
import IdeaCanvasBox from "./IdeaCanvasBox";
import { MessageCodeView } from "../ai_home/ChatBox";
import TokenManagerModal from "../finance/TokenManagerModal";

// step: idle, analysed, generated

const { TextArea } = Input;
const { Text, Title, Paragraph } = Typography;

const POST_TYPE_MAPPING = {
    instagram_post: "INSTAGRAM_POST",
    instagram_story: "INSTAGRAM_STORY",
    instagram_reel: "INSTAGRAM_REEL",
    facebook_post: "FACEBOOK_POST",
    facebook_story: "FACEBOOK_STORY",
    twitter_post: "TWITTER_POST",
    linkedin_post: "LINKEDIN_POST",
    linkedin_story: "LINKEDIN_STORY",
    x_post: "X_POST",
};

const CreationFromLinkBox = forwardRef(({ trigger, me }, ref) => {
    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();
    const isMobile = !screens.sm;

    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("links");
    const [input, setInput] = useState({});
    const [analysed_result, setAnalysedResult] = useState(null);
    const [open_ideas, setOpenIdeas] = useState(false);
    const [file, setFile] = useState(null);
    const [generationType, setGenerationType] = useState("normal");
    const [hasValidationError, setHasValidationError] = useState(false); // Add this state
    const [serverError, setServerError] = useState(""); // Add server error state
    const [openTokenModal, setOpenTokenModal] = useState(false);

    useImperativeHandle(ref, () => ({
        open: (tabId) => {
            setOpen(true);
            setActiveTab(tabId || "link"); // Use passed tab or default to 'links'
        },
    }));
    const mutationAnalyse = useAnalyseDataSource(
        (result) => {
            log.d(result, "Idea gen markdown Result");
            if (result?.success === true) {
                setAnalysedResult(result?.data);
                setOpenIdeas(true);
                setOpen(false);
                setServerError(""); // Clear error on success
            }
            if (
                result?.success === false &&
                result?.error?.code === "TOKEN_INSUFFICIENT"
            ) {
                setOpen(false);
                message.error(result?.error?.msg);
                setOpenTokenModal(true);
            }
        },
        (error) => {
            // Handle error callback
            log.e(error, "Analysis failed");
            const errorMessage =
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to analyze the content. Please try again.";
            setServerError(errorMessage);
            message.error(errorMessage);
        }
    );

    log.d(analysed_result, "Analysed Result");
    const mutationUpload = useUploadFile(
        (data) => {
            setInput({
                doc: data?.id,
            });
            message.success("file uploaded successfully");
        },
        (error) => {
            message.error("failed to upload" + error.message);
        }
    );
    const handleUpload = ({ file }) => {
        if (file) {
            const formData = new FormData();
            formData.append("file", file);
            mutationUpload.mutate(formData);
        }
    };

    return (
        <>
            {trigger && (
                <div
                    onClick={() => {
                        setOpen(true);
                        setActiveTab("link");
                    }}
                >
                    {trigger}
                </div>
            )}
            <Modal
                open={open}
                onCancel={() => setOpen(false)}
                title="Generate Creation"
                width={isMobile ? "100%" : 1000}
                okButtonProps={{
                    size: isMobile ? "large" : "middle",
                    icon: <ArrowRightOutlined />,
                    iconPosition: "end",
                    loading: mutationAnalyse.isLoading,
                }}
                cancelButtonProps={{ size: isMobile ? "large" : "middle" }}
                footer={[
                    <Button key="back" onClick={() => setOpen(false)}>
                        Cancel
                    </Button>,
                    <Button
                        type="primary"
                        disabled={hasValidationError}
                        onClick={() => {
                            const hasValidInput =
                                input &&
                                Object.keys(input).length > 0 &&
                                Object.values(input).some((value) => {
                                    if (typeof value === "string") {
                                        return value.trim().length > 0;
                                    }
                                    return (
                                        value !== null &&
                                        value !== "" &&
                                        value !== undefined
                                    );
                                });

                            if (!hasValidInput) {
                                message.error(
                                    "Please enter resource before analyzing"
                                );
                                return;
                            }
                            if (!hasValidationError) {
                                console.log(
                                    "Starting mutation with input:",
                                    input
                                ); // Debug log

                                mutationAnalyse.mutate({
                                    ...input,
                                    idea_type: generationType,
                                });
                            }
                        }}
                        loading={mutationAnalyse.isLoading}
                    >
                        Analyse
                    </Button>,
                ]}
            >
                <Tabs
                    type="card"
                    items={[
                        {
                            key: "link",
                            label: "From Link",
                            children: (
                                <GenerateFromLink
                                    onChange={(new_val) =>
                                        setInput({ link: new_val })
                                    }
                                    onValidationChange={setHasValidationError} // Pass validation state
                                />
                            ),
                        },
                        {
                            key: "text",
                            label: "From Text",
                            children: (
                                <GenerateFromText
                                    onChange={(new_val) =>
                                        setInput({ text: new_val })
                                    }
                                />
                            ),
                        },
                        {
                            key: "document",
                            label: "From Document",
                            children: (
                                <GenerateFromDocument
                                    handleUpload={handleUpload}
                                    setInput={setInput}
                                />
                            ),
                        },
                    ]}
                    activeKey={activeTab}
                    onChange={(key) => setActiveTab(key)}
                />
                <div style={{ marginBottom: "16px" }}></div>
            </Modal>
            <GeneratedIdeasBox
                open={open_ideas}
                setOpen={setOpenIdeas}
                result={analysed_result}
                me={me}
                generation_type={generationType}
            />
            <TokenManagerModal
                selected_org={me?.selected_org?.id}
                open={openTokenModal}
                onClose={() => setOpenTokenModal(false)}
            />
        </>
    );
});

export default CreationFromLinkBox;

function GenerateFromLink({ onChange, onValidationChange }) {
    const [error, setError] = useState("");

    const handleChange = (e) => {
        const value = e.target.value.trim();
        let hasError = false;

        // Simple validation
        if (value && !isValidUrl(value)) {
            setError("Please enter a valid URL");
            hasError = true;
        } else {
            setError("");
            hasError = false;
        }

        // Notify parent about validation state
        onValidationChange(hasError);
        onChange(value);
    };

    const isValidUrl = (url) => {
        try {
            let trimmed = url.trim();

            // Auto prepend https:// if it starts with www.
            if (trimmed.startsWith("www.")) {
                trimmed = "https://" + trimmed;
            }

            // If no protocol, add https://
            if (
                !trimmed.startsWith("http://") &&
                !trimmed.startsWith("https://")
            ) {
                trimmed = "https://" + trimmed;
            }

            const parsed = new URL(trimmed);

            if (!["http:", "https:"].includes(parsed.protocol)) {
                return false;
            }

            // Check for valid domain format
            const domain = parsed.hostname;

            // Must have at least one dot and valid TLD
            const domainParts = domain.split(".");
            if (domainParts.length < 2) {
                return false;
            }

            // Last part should be valid TLD (at least 2 characters)
            const tld = domainParts[domainParts.length - 1];
            if (tld.length < 2 || !/^[a-zA-Z]+$/.test(tld)) {
                return false;
            }

            // Domain parts should not be empty and contain valid characters
            for (const part of domainParts) {
                if (!part || !/^[a-zA-Z0-9-]+$/.test(part)) {
                    return false;
                }
            }

            return true;
        } catch (err) {
            return false;
        }
    };
    return (
        <Form layout="vertical">
            <Form.Item
                label="External Link"
                validateStatus={error ? "error" : ""}
                help={error}
            >
                <Input
                    onChange={handleChange}
                    placeholder="Enter valid link (e.g., https://example.com)"
                    status={error ? "error" : ""}
                />
            </Form.Item>
        </Form>
    );
}

function GenerateFromText({ onChange }) {
    return (
        <Form layout="vertical">
            <Form.Item label="Write your idea">
                <TextArea rows={4} onChange={(e) => onChange(e.target.value)} />
            </Form.Item>
        </Form>
    );
}

function GenerateFromDocument({ handleUpload, setInput }) {
    const [file, setFile] = useState(null);
    const [uploadError, setUploadError] = useState("");

    const allowedExtensions = ["pdf", "doc", "docx", "txt", "csv"];

    const validateFile = (uploadFile) => {
        // Additional check for file extension
        const fileExtension = uploadFile.name.split(".").pop().toLowerCase();
        if (!allowedExtensions.includes(fileExtension)) {
            setUploadError(
                "Only PDF, DOC, DOCX, CSV and TXT files are supported"
            );
            return false;
        }

        // Check file size (optional - 10MB limit)
        const maxSize = 5 * 1024 * 1024; // 10MB
        if (uploadFile.size > maxSize) {
            setUploadError("File size must be less than 5MB");
            return false;
        }

        setUploadError("");
        return true;
    };

    const handleFileUpload = (uploadFile) => {
        if (validateFile(uploadFile)) {
            setFile(uploadFile); // Set the file state here
            handleUpload({ file: uploadFile });
        }
        return false; // Prevent default upload behavior
    };

    const getFileIcon = (fileName) => {
        if (!fileName) return FileOutlined;

        const extension = fileName.split(".").pop().toLowerCase();

        switch (extension) {
            case "pdf":
                return FilePdfOutlined;
            case "doc":
            case "docx":
                return FileWordOutlined;
            case "txt":
                return FileTextOutlined;
            default:
                return FileOutlined;
        }
    };

    const removeFile = () => {
        setFile(null);
        setUploadError("");
    };

    return (
        <Form layout="vertical">
            <Form.Item
                label="Document"
                validateStatus={uploadError ? "error" : ""}
                help={uploadError}
            >
                {!file ? (
                    <Upload.Dragger
                        beforeUpload={handleFileUpload}
                        showUploadList={false}
                        className={styles.modalDragger}
                    >
                        <div className={styles.uploadContent}>
                            <CloudUploadOutlined
                                className={styles.uploadIcon}
                            />
                            <div className={styles.uploadText}>
                                <span className={styles.uploadMainText}>
                                    Drag & drop files here or{" "}
                                    <span className={styles.browseText}>
                                        browse
                                    </span>
                                </span>
                                <span className={styles.uploadHintText}>
                                    PDF, DOC, DOCX, TXT up to 10MB
                                </span>
                            </div>
                        </div>
                    </Upload.Dragger>
                ) : (
                    <div className={styles.fileUploaded}>
                        <div className={styles.fileInfo}>
                            {React.createElement(getFileIcon(file.name), {
                                className: styles.fileTypeIcon,
                                style: {
                                    fontSize: "30px",
                                },
                            })}
                            <div className={styles.fileDetails}>
                                <Typography.Text
                                    strong
                                    className={styles.fileName}
                                >
                                    {file.name}
                                </Typography.Text>
                                <Typography.Text className={styles.fileSize}>
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                </Typography.Text>
                            </div>
                            <Button
                                type="text"
                                icon={
                                    <DeleteOutlined
                                        className={styles.removeIcon}
                                    />
                                }
                                onClick={removeFile}
                                className={styles.removeButton}
                            />
                        </div>
                    </div>
                )}
            </Form.Item>
        </Form>
    );
}
function GenerateFromImage() {
    return <p>Image</p>;
}

export function GeneratedIdeasCore({
    result,
    me,
    generation_type,
    platforms = [],
}) {
    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();
    const isMobile = !screens.sm;
    const [selected_idea, setSelectedIdea] = useState(null);
    const [generated_idea, setGeneratedIdea] = useState(null);

    // const [selected_card, setSelectedCard] = useState(null);

    const [open_idea, setOpenIdea] = useState(false);
    const [genertaedPreset, setGeneratedPreset] = useState(null);
    const [openPresetModal, setOpenPresetModal] = useState(false); // Add this state
    const [openTokenModal, setOpenTokenModal] = useState(false);

    const mutationIdeaGen = useTrendIdeaGeneration((result) => {
        log.d(result, "Gen Idea Result");
        if (result?.success === true && result?.data?.status === "stuck") {
            message.error("Template have issue");
            setSelectedIdea(null);
        } else {
            setGeneratedIdea(result?.data);
            setOpenIdea(true);
            setSelectedIdea(null);
        }
        if (
            result?.success === false &&
            result?.error?.code === "TOKEN_INSUFFICIENT"
        ) {
            message.error(result?.error?.msg);
            setOpenTokenModal(true);
        }
    });

    const mutationNewPresetGen = useNewPresetGeneration((result) => {
        log.d(result, "Gen New Preset Result");
        setGeneratedPreset(result);
        setOpenPresetModal(true); // Open the preset modal when data is received
    });
    const createPresetMutation = useCreateItem(
        "qdb_creation_schema",
        (data) => {
            console.log("item created successfully", data);
        }
    );

    useEffect(() => {
        if (selected_idea) {
            const platform = selected_idea?.platform;
            const platformFull = platforms.filter(
                (plf) => plf.platform.toLowerCase() === platform
            );
            const out = {
                idea: selected_idea,
                data_source_id: result?.id,
                platforms: platformFull.map((pf) => ({
                    id: pf.id,
                    platform: pf.platform,
                    post_type: POST_TYPE_MAPPING[selected_idea?.content_type],
                    posting_account: pf.id,
                    scheduled_time: "2026-04-10T12:30:00",
                })),
            };
            log.d(out, "out in useMemo");
            mutationIdeaGen.mutate(out);
            setGeneratedIdea(null);
        }
        // }, [selected_idea, mutationIdeaGen, platforms, result?.id]);
    }, [selected_idea]);

    const onIdeaSelect = (idea) => {
        const out = {
            idea: idea,
            data_source_id: result?.id,
        };
        mutationIdeaGen.mutate(out);

        log.d(out, "on Selected Idea");
    };

    const handlePresetGen = (idea) => {
        const out = { idea: idea?.idea };
        mutationNewPresetGen.mutate(out);
        log.d(out, "on selected idea for preset gen");
    };

    const handlePresetSave = (preset) => {
        const payload = {
            block_id: preset.name,
            name: preset.name,
            // description: formValues.description,
            type: "singlepage",
            // inputs: modified_inputs,
            true_schema: { preset },
            enabled: false,
        };
        createPresetMutation.mutate(payload, {
            onSuccess: () => {
                message.success("Preset created successfully!");
            },
            onError: (error) => {
                console.error("Failed to create preset:", error);
                message.error("Failed to create preset");
            },
        });
    };

    return (
        <>
            <Tabs
                type="card"
                items={[
                    {
                        key: "generated_ideas",
                        label: "Generated Ideas",
                        children: (
                            <>
                                {result?.generated_ideas?.length > 0 && (
                                    <div className={styles.ideas_wrapper}>
                                        {result?.generated_ideas?.map(
                                            (idea, index) => (
                                                <div
                                                    key={index}
                                                    style={{
                                                        marginBottom:
                                                            generation_type ===
                                                            "extra"
                                                                ? "16px"
                                                                : "0",
                                                    }}
                                                >
                                                    <CalendarCard
                                                        item={{
                                                            ...idea,
                                                            content: idea?.idea,
                                                            // platform:
                                                            //     toLower(
                                                            //         idea?.platform
                                                            //     ),
                                                            platforms: [
                                                                {
                                                                    post_type:
                                                                        idea?.content_type,
                                                                    posting_time:
                                                                        idea?.posting_time,
                                                                    status: "IDEA",
                                                                },
                                                            ],
                                                        }}
                                                        loading={
                                                            selected_idea?.idea ===
                                                            idea?.idea
                                                        }
                                                        onClick={() => {
                                                            // setOpen(true);
                                                            setSelectedIdea({
                                                                ...idea,
                                                                index: index,
                                                            });
                                                            // setSelectedIdea(
                                                            //     idea
                                                            // );
                                                            // onIdeaSelect(
                                                            //     idea
                                                            // );
                                                        }}
                                                    />
                                                    {generation_type ===
                                                        "extra" && (
                                                        <Button
                                                            // type="primary"
                                                            size={
                                                                isMobile
                                                                    ? "large"
                                                                    : "middle"
                                                            }
                                                            block={isMobile}
                                                            style={{
                                                                width: isMobile
                                                                    ? "100%"
                                                                    : "40%",
                                                                marginTop:
                                                                    "8px",
                                                            }}
                                                            onClick={() => {
                                                                setSelectedIdea(
                                                                    idea
                                                                );
                                                                handlePresetGen(
                                                                    idea
                                                                );
                                                            }}
                                                        >
                                                            Generate Preset with
                                                            AI
                                                        </Button>
                                                    )}
                                                </div>
                                            )
                                        )}
                                    </div>
                                )}
                                {/* {selected_idea && (
                                    <MessageCodeView
                                        data={{
                                            language: "json",
                                            code: selected_idea,
                                            message: "Selected Idea",
                                        }}
                                    />
                                )} */}
                            </>
                        ),
                    },
                    {
                        key: "extracted_text",
                        label: "Extracted Text",
                        children: (
                            <div>
                                {result?.type === "link" &&
                                    result?.extracted_data?.markdown && (
                                        <ReactMarkdown>
                                            {result.extracted_data.markdown}
                                        </ReactMarkdown>
                                    )}

                                {result?.type === "document" &&
                                    result?.extracted_data && (
                                        <ReactMarkdown>
                                            {result.extracted_data}
                                        </ReactMarkdown>
                                    )}
                            </div>
                        ),
                    },
                ]}
            />
            <TokenManagerModal
                selected_org={me?.selected_org?.id}
                open={openTokenModal}
                onClose={() => setOpenTokenModal(false)}
            />
            <Modal
                open={openPresetModal}
                onCancel={() => setOpenPresetModal(false)}
                title="Generated Preset"
                width={800}
                footer={[
                    <Button
                        key="close"
                        onClick={() => setOpenPresetModal(false)}
                    >
                        Close
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        onClick={() => {
                            // Add save logic here if needed
                            // handlePresetSave(genertaedPreset);
                            message.success("Preset saved successfully!");
                            setOpenPresetModal(false);
                        }}
                    >
                        Save Preset
                    </Button>,
                ]}
            >
                {" "}
                {genertaedPreset && (
                    <div>
                        <Typography.Title level={4}>
                            Preset Details
                        </Typography.Title>
                        <pre
                            style={{
                                backgroundColor: "#f5f5f5",
                                padding: "16px",
                                borderRadius: "4px",
                                whiteSpace: "pre-wrap",
                                maxHeight: "400px",
                                overflow: "auto",
                            }}
                        >
                            {JSON.stringify(genertaedPreset, null, 2)}
                        </pre>
                    </div>
                )}
            </Modal>

            {generated_idea && (
                <IdeaCanvasBox
                    key={generated_idea}
                    open={open_idea}
                    setOpen={setOpenIdea}
                    data={generated_idea}
                    entry_id={generated_idea?.id}
                    // data={{
                    //     status: "generated",
                    //     generated_data: generated_idea?.generated_data,
                    // }}
                    me={me}
                />
            )}
        </>
    );
}

export function GeneratedIdeasBox({
    open,
    setOpen,
    result,
    me,
    generation_type,
}) {
    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();
    const isMobile = !screens.sm;

    return (
        <Modal
            open={open}
            onCancel={() => setOpen(false)}
            title="Generated Ideas"
            width={isMobile ? "100%" : 1000}
            footer={[]}
            maskClosable={false}
        >
            <GeneratedIdeasCore
                result={result}
                me={me}
                generation_type={generation_type}
            />
        </Modal>
    );
}
